import type { CoreStoreType } from '../../store/CoreStore';

export interface GameCommunicationErrorMessageType extends GameCommunicationMessageType {
  optExtErrorCode?: string;
  optExtErrorMsg?: string;
}

export interface GameCommunicationMessageType extends Record<string, unknown> {
  beforeInit: boolean;
  delay: number;
  seq: number;
}

export type GameCommunicationSenderType =
  | ((data: string | ArrayBufferLike | Blob | ArrayBufferView) => void)
  | undefined;

export interface GameCommunicationMetaType {
  connectionStatus: ConnectionStatus;
  connectionProcedure: () => void;
}

export interface IGameCommunicationProcessor {
  messageName: string;
  delay?: number;
  /* A number value that represents the delay in milliseconds before the processor runs. */
  delayCorrection?: number;

  /* A number value that represents the correction to be applied to the delay before the processor runs. */
  process(
    message: GameCommunicationMessageType,
    store: CoreStoreType,
    sender: GameCommunicationSenderType,
    meta: GameCommunicationMetaType
  ): void;
}

export interface IGameCommunicationSender<Payload> {
  name: string;
  dispatch(payload: Payload): void;
  send(payload: Payload, store: CoreStoreType, sender: GameCommunicationSenderType): void;
}

export interface IGameCommunicationConnectionObserver {
  update(connectedStatus: ConnectionStatus, store: CoreStoreType): void;
}

export interface GameCommunicationControllerConfig {
  processors?: IGameCommunicationProcessor[];
  senders?: IGameCommunicationSender<unknown>[];
  connectionObserver?: IGameCommunicationConnectionObserver;
}

export interface GameCommunicationControllerConnectorProps {
  gameStore: CoreStoreType;
  config?: GameCommunicationControllerConfig;
}

export interface GameMessage extends GameCommunicationMessageType {
  id?: string;
}

export interface TableCloseMessage extends GameCommunicationMessageType {
  openTime?: number;
}

export interface GameCommunicationTimeout {
  timeout: NodeJS.Timeout;
  messageName: string;
  process: () => void;
  seq: number;
  queuedGameId: string | null;
}

export enum ConnectionStatus {
  DISCONNECTED, // When connection is not yet established or was stopped (stopStart method in GameCommunicationController)
  CONNECTING, // When connection is being established
  CONNECTED, // When connection is established
  RECONNECTING, // When connection is lost and trying to reconnect
  RECONNECTED, // When connection is re-established after being lost
  CONNECTION_LOST, // When failed to reconnect after a certain number of attempts
  NEW_ROUND, // When a new round is started
}
